import type { SpanJSON, TransactionEvent } from "@sentry/core";
import { shouldAlwaysSampleSentrySession } from "@/utils/sentryUserContext";

/**
 * Sentry performance policy: page-level only.
 * Send navigation + pageload transactions and INP interaction spans (how fast
 * a click gets a response); drop every child/asset/browser span.
 */

const PAGE_LEVEL_OPS = new Set(["navigation", "pageload"]);

const STATIC_ASSET_TRANSACTION_RE =
  /\.(js|css|mjs|map|woff2?|png|jpe?g|svg|webp|gif|ico)(\?|$)|\/(js|css|img|fonts)\//i;

/** All resource-timing categories Sentry can emit as child spans. */
export const SENTRY_IGNORE_RESOURCE_SPANS = [
  "resource.script",
  "resource.css",
  "resource.link",
  "resource.img",
  "resource.font",
  "resource.media",
  "resource.other",
  "resource.beacon",
  "resource.wasm",
  "resource.fetch",
  "resource.xhr",
] as const;

/** Drop non-page spans through the supported SDK option, not beforeSendSpan. */
export const SENTRY_IGNORE_SPANS = [
  { op: /^(?!(navigation|pageload|ui\.interaction\.[a-z]+)$).*$/ },
] as const;

/**
 * Requests that say nothing about how long a page took. Every traced request
 * keeps a pageload or navigation open until 1s after it ends, so these made
 * page durations run to the 30s cap:
 * - Firestore's long-poll back channel (`RID=rpc`) and RTDB long polling stay
 *   open for as long as the page does;
 * - analytics beacons fire whenever the user interacts.
 * The Firestore requests that carry actual reads and writes are still traced.
 */
const UNTIMED_REQUEST_RE =
  /firestore\.googleapis\.com\/.*\/channel\?.*\bRID=rpc\b|(firebaseio\.com|firebasedatabase\.app)\/\.lp\b|googletagmanager\.com|google-analytics\.com|analytics\.google\.com|facebook\.(com|net)|doubleclick\.net|mixpanel\.com|clarity\.ms|hotjar\.(com|io)/i;

export function shouldCreateSpanForRequest(url = ""): boolean {
  return !UNTIMED_REQUEST_RE.test(url);
}

export function isStaticAssetTransaction(name = ""): boolean {
  return STATIC_ASSET_TRANSACTION_RE.test(name);
}

export function isPageLevelSpan(span: Pick<SpanJSON, "op">): boolean {
  return PAGE_LEVEL_OPS.has(span.op ?? "");
}

/** Drop asset transactions and strip child spans before ingest. */
export function filterPerformanceTransaction(
  event: TransactionEvent
): TransactionEvent | null {
  const transactionName = event.transaction ?? "";
  if (isStaticAssetTransaction(transactionName)) {
    return null;
  }

  const rootOp = event.contexts?.trace?.op ?? "";
  if (rootOp && !PAGE_LEVEL_OPS.has(rootOp)) {
    return null;
  }

  if (event.spans?.length) {
    event.spans = event.spans.filter((span) => isPageLevelSpan(span));
  }

  return event;
}

export function getTracesSampleRate(): number {
  if (process.env.NODE_ENV !== "production") {
    return 1.0;
  }
  return shouldAlwaysSampleSentrySession() ? 0.3 : 0.2;
}

export function getReplaySessionSampleRate(): number {
  if (process.env.NODE_ENV !== "production") {
    return 1.0;
  }
  return shouldAlwaysSampleSentrySession() ? 0.2 : 0.1;
}
